/** Shared, dependency-free wire contract. Also imported by the web renderer. */
export const SKIN_FAMILIES = [
  "gps",
  "timer",
  "pylon",
  "lowerThird",
  "playerCard",
  "scoreboard",
  "billboard",
  "podium",
  "startList",
] as const;
export type SkinFamily = (typeof SKIN_FAMILIES)[number];
export const SKIN_PARTS = ["surface", "header", "badge", "accent"] as const;
export type SkinPart = (typeof SKIN_PARTS)[number];
export type SkinColorToken = "surface" | "accent" | "text";
export interface SkinGeometry {
  radius?: number;
  polygon?: [number, number][];
  border?: { width: number; color: SkinColorToken };
  edge?: {
    side: "left" | "right" | "top" | "bottom";
    width: number;
    color: SkinColorToken;
  };
  paddingX?: number;
}
export interface SkinFamilyGeometry {
  gap?: number;
  parts?: Partial<Record<SkinPart, SkinGeometry>>;
}
export interface OverlaySkinSpecV1 {
  version: 1;
  defaults?: SkinFamilyGeometry;
  families?: Partial<Record<SkinFamily, SkinFamilyGeometry>>;
}
export interface OverlayCustomSkinReference {
  skinID: string;
  revisionID: string;
}
export interface OverlaySkinSnapshot {
  name: string;
  spec: OverlaySkinSpecV1;
}
export type OverlaySkinSnapshots = Record<string, OverlaySkinSnapshot>;
export interface LeagueOverlaySkin {
  id: string;
  name: string;
  description: string;
  latestRevisionID: string;
  archived: boolean;
  createdByUserID: string;
  createdAt?: unknown;
  updatedAt?: unknown;
}
export interface OverlaySkinRevision extends OverlaySkinSnapshot {
  id: string;
  createdByUserID: string;
  createdAt?: unknown;
}
export const SKIN_SPEC_MAX_BYTES = 24_000;
export const SKIN_SNAPSHOTS_MAX_BYTES = 240_000;
const ID = /^[A-Za-z0-9_-]{1,80}$/;
export function skinIDValid(value: unknown): value is string {
  return typeof value === "string" && ID.test(value);
}
export function skinRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}
export function skinJSONBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}
export function parseCustomSkinReference(
  value: unknown,
): OverlayCustomSkinReference | undefined {
  if (
    !skinRecord(value) ||
    Object.keys(value).some((k) => !["skinID", "revisionID"].includes(k)) ||
    !skinIDValid(value.skinID) ||
    !skinIDValid(value.revisionID)
  )
    return undefined;
  return { skinID: value.skinID, revisionID: value.revisionID };
}
export function skinSnapshotKey(ref: OverlayCustomSkinReference): string {
  return `${ref.skinID}:${ref.revisionID}`;
}
export function skinSpecIssues(raw: unknown): string[] {
  const issues: string[] = [];
  const object = (
    value: unknown,
    path: string,
    keys: readonly string[],
  ): value is Record<string, unknown> => {
    if (!skinRecord(value)) {
      issues.push(`${path}: expected an object`);
      return false;
    }
    for (const k of Object.keys(value))
      if (!keys.includes(k)) issues.push(`${path}.${k}: unsupported field`);
    return true;
  };
  const number = (v: unknown, path: string, min: number, max: number) => {
    if (typeof v !== "number" || !Number.isFinite(v) || v < min || v > max)
      issues.push(`${path}: expected ${min}..${max}`);
  };
  const color = (v: unknown, path: string) => {
    if (!["surface", "accent", "text"].includes(v as string))
      issues.push(`${path}: expected surface, accent, or text`);
  };
  const family = (v: unknown, path: string) => {
    if (!object(v, path, ["gap", "parts"])) return;
    if (v.gap !== undefined) number(v.gap, `${path}.gap`, 0, 12);
    if (v.parts === undefined || !object(v.parts, `${path}.parts`, SKIN_PARTS))
      return;
    for (const [part, g] of Object.entries(v.parts)) {
      const p = `${path}.parts.${part}`;
      if (!object(g, p, ["radius", "polygon", "border", "edge", "paddingX"]))
        continue;
      if (g.radius !== undefined) number(g.radius, `${p}.radius`, 0, 40);
      if (g.paddingX !== undefined) number(g.paddingX, `${p}.paddingX`, 0, 48);
      if (g.radius !== undefined && g.polygon !== undefined)
        issues.push(`${p}: choose radius or polygon`);
      if (g.polygon !== undefined) {
        if (
          !Array.isArray(g.polygon) ||
          g.polygon.length < 3 ||
          g.polygon.length > 12
        )
          issues.push(`${p}.polygon: expected 3..12 points`);
        else
          g.polygon.forEach((point, i) => {
            if (!Array.isArray(point) || point.length !== 2)
              issues.push(`${p}.polygon[${i}]: expected [x,y]`);
            else
              point.forEach((n, axis) =>
                number(n, `${p}.polygon[${i}][${axis}]`, 0, 100),
              );
          });
      }
      if (
        g.border !== undefined &&
        object(g.border, `${p}.border`, ["width", "color"])
      ) {
        number(g.border.width, `${p}.border.width`, 0, 8);
        color(g.border.color, `${p}.border.color`);
      }
      if (
        g.edge !== undefined &&
        object(g.edge, `${p}.edge`, ["side", "width", "color"])
      ) {
        number(g.edge.width, `${p}.edge.width`, 0, 16);
        color(g.edge.color, `${p}.edge.color`);
        if (!["left", "right", "top", "bottom"].includes(g.edge.side as string))
          issues.push(`${p}.edge.side: invalid side`);
      }
    }
  };
  if (!object(raw, "spec", ["version", "defaults", "families"])) return issues;
  if (raw.version !== 1) issues.push("spec.version: must be 1");
  if (raw.defaults !== undefined) family(raw.defaults, "spec.defaults");
  if (
    raw.families !== undefined &&
    object(raw.families, "spec.families", SKIN_FAMILIES)
  ) {
    for (const [key, value] of Object.entries(raw.families))
      family(value, `spec.families.${key}`);
  }
  if (skinJSONBytes(raw) > SKIN_SPEC_MAX_BYTES)
    issues.push(`spec: exceeds ${SKIN_SPEC_MAX_BYTES} JSON bytes`);
  return issues;
}
export function requireSkinSpec(raw: unknown): OverlaySkinSpecV1 {
  const issues = skinSpecIssues(raw);
  if (issues.length) throw new Error(issues.join("; "));
  return JSON.parse(JSON.stringify(raw)) as OverlaySkinSpecV1;
}
export interface StoredSkinGeometry extends Omit<SkinGeometry, "polygon"> {
  polygon?: { x: number; y: number }[];
}
export interface StoredSkinFamilyGeometry extends Omit<SkinFamilyGeometry, "parts"> {
  parts?: Partial<Record<SkinPart, StoredSkinGeometry>>;
}
export interface StoredOverlaySkinSpecV1 {
  version: 1;
  defaults?: StoredSkinFamilyGeometry;
  families?: Partial<Record<SkinFamily, StoredSkinFamilyGeometry>>;
}
export type StoredOverlaySkinSnapshots = Record<string, {
  name: string;
  spec: StoredOverlaySkinSpecV1;
}>;

/** Only transform polygon points; leave all other fields for strict validation. */
function mapSkinPoints(raw: unknown, point: (value: unknown) => unknown): unknown {
  if (!skinRecord(raw)) return raw;
  const family = (value: unknown): unknown => {
    if (!skinRecord(value) || !skinRecord(value.parts)) return value;
    return { ...value, parts: Object.fromEntries(Object.entries(value.parts).map(([key, part]) => [
      key, skinRecord(part) && Array.isArray(part.polygon)
        ? { ...part, polygon: part.polygon.map(point) }
        : part,
    ])) };
  };
  return {
    ...raw,
    ...(raw.defaults !== undefined ? { defaults: family(raw.defaults) } : {}),
    ...(skinRecord(raw.families) ? { families: Object.fromEntries(
      Object.entries(raw.families).map(([key, value]) => [key, family(value)]),
    ) } : {}),
  };
}
/** Public v1 uses tuples; Firestore prohibits arrays directly nested in arrays. */
export function skinSpecFirestoreData(raw: OverlaySkinSpecV1): StoredOverlaySkinSpecV1 {
  const stored = mapSkinPoints(requireSkinSpec(raw), (point) => {
    const [x, y] = point as [number, number];
    return { x, y };
  }) as StoredOverlaySkinSpecV1;
  if (skinJSONBytes(stored) > SKIN_SPEC_MAX_BYTES)
    throw new Error(`spec: encoded Firestore representation exceeds ${SKIN_SPEC_MAX_BYTES} JSON bytes`);
  return stored;
}
/** Accept legacy tuple fixtures/imports as well as persisted map points. */
export function requireStoredSkinSpec(raw: unknown): OverlaySkinSpecV1 {
  if (skinRecord(raw) && skinJSONBytes(raw) > SKIN_SPEC_MAX_BYTES)
    throw new Error(`spec: stored representation exceeds ${SKIN_SPEC_MAX_BYTES} JSON bytes`);
  return requireSkinSpec(mapSkinPoints(raw, (point) =>
    skinRecord(point) && Object.keys(point).length === 2 &&
      Object.prototype.hasOwnProperty.call(point, "x") && Object.prototype.hasOwnProperty.call(point, "y")
      ? [point.x, point.y] : point,
  ));
}
export function skinSnapshotsFirestoreData(raw: OverlaySkinSnapshots): StoredOverlaySkinSnapshots {
  return Object.fromEntries(Object.entries(raw).map(([key, snapshot]) => [
    key, { name: snapshot.name, spec: skinSpecFirestoreData(snapshot.spec) },
  ]));
}
/** Invalid entries remain unavailable to rendering; never use this to rewrite stored maps. */
export function parseStoredSkinSnapshots(raw: unknown): OverlaySkinSnapshots {
  if (!skinRecord(raw) || skinJSONBytes(raw) > SKIN_SNAPSHOTS_MAX_BYTES) return {};
  const decoded = Object.fromEntries(Object.entries(raw).map(([key, value]) => {
    if (!skinRecord(value)) return [key, value];
    try { return [key, { ...value, spec: requireStoredSkinSpec(value.spec) }]; }
    catch { return [key, value]; }
  }));
  return parseSkinSnapshots(decoded);
}
export function parseSkinSnapshots(raw: unknown): OverlaySkinSnapshots {
  if (!skinRecord(raw) || skinJSONBytes(raw) > SKIN_SNAPSHOTS_MAX_BYTES)
    return {};
  const result: OverlaySkinSnapshots = {};
  for (const [key, value] of Object.entries(raw)) {
    const [skinID, revisionID, extra] = key.split(":");
    if (
      extra !== undefined ||
      !skinIDValid(skinID) ||
      !skinIDValid(revisionID) ||
      !skinRecord(value) ||
      typeof value.name !== "string" ||
      value.name.length > 80
    )
      continue;
    if (skinSpecIssues(value.spec).length === 0)
      result[key] = { name: value.name, spec: value.spec as OverlaySkinSpecV1 };
  }
  return result;
}
export const SKIN_SPEC_GUIDE = {
  version: 1,
  families: SKIN_FAMILIES,
  parts: SKIN_PARTS,
  geometry:
    "radius: 0..40 OR polygon: 3..12 [xPercent,yPercent] points in 0..100; border: {width:0..8,color}; edge:{side:left|right|top|bottom,width:0..16,color}; paddingX:0..48. Colors: surface|accent|text. Family gap:0..12. No CSS, URLs, font sizes or bar heights. Parts are painted inner surfaces; family overrides replace a default part in full.",
  lifecycle:
    "Creation publishes to the league catalogue only. Revisions are immutable. Applying a specific revision is a separate approved action. Existing overlays never auto-upgrade.",
  palette:
    "Skins define geometry, not literal colors. surface, accent and text refer to the overlay's existing colors; a description does not change its palette. Submit polygon points as [x,y] tuples; storage encoding is handled by the service.",
  maxJSONBytes: SKIN_SPEC_MAX_BYTES,
  sizeLimit:
    "The JSON byte limit applies to both the submitted spec and its Firestore encoding; polygon point maps occupy more bytes than public tuples. Preparation checks both sizes before approval.",
  example: {
    version: 1,
    defaults: {
      gap: 4,
      parts: {
        surface: {
          radius: 8,
          edge: { side: "left", width: 5, color: "accent" },
        },
        badge: {
          polygon: [
            [0, 0],
            [100, 0],
            [100, 75],
            [50, 100],
            [0, 75],
          ],
        },
      },
    },
  },
};
